/* eslint-disable indent */
import { gql } from 'graphql-request';
import { For, Show, createEffect, createMemo, createSignal, on, onMount, useContext } from 'solid-js';
import { AppContext } from '../../app-context-provider/app-context-provider';
import theme from '../../style/theme';
import { MyContentItem, getMyContent } from './my-content-helpers';
import { Heading } from '../../ui-components/heading/heading';
import { Tabnav, TabnavItem } from '../../ui-components/tabnav/tabnav';
import decodeHtmlEntities from '../../tools/decode-html-entities';
import { Text } from '../../ui-components/text/text';
import { BlogNewsData } from '../blog-news/blog-news';
import { createGroupedBlogAndNewsCard } from '../blogs-news/blogs-news-component';
import { EventData } from '../event/event-types';
import { AngleDownIcon } from '../icons-library/angle-down';
import { PagePostCard } from '../page/page-post-card';
import { ProductData } from '../product/product-types';
import { createGroupedProductCard } from '../products/products';
import { createGroupedVideoCard } from '../videos/videos-component';
import { VideoData } from '../videos/videos-types';
import { createGroupedEventCard } from '../events/helpers/events-card-helper';

const GET_CONTENT_QUERY = gql`
    query GetContent($key: String!) {
        resource(key: $key) {
            content
            key
        }
    }
`;

type MyContentProps = {
    lastElement?: (el: any) => void;
    filterBgColor?: string;
};

export const MyContent = (props: MyContentProps) => {
    const AppState = useContext(AppContext);
    const { graphQlClient, viewport, localize, /* pdfBuilderPage */ } = useContext(AppContext);
    const isMobile = () => viewport.width <= theme.breakpoints.MOBILE;

    const POSTS_PER_PAGE_MOBILE = 3;
    const [ currentPage, setCurrentPage ] = createSignal({
        events: 1,
        news: 1,
        products: 1,
        videos: 1,
        blogs: 1,
        pages: 1,
    });

    const [ content, setContent ] = createSignal([] as MyContentItem[]);
    const [ fetchedData, setFetchedData ] = createSignal([] as any);
    const [ loading, setLoading ] = createSignal(true);
    const [ selectedCategory, setSelectedCategory ] = createSignal<any>({
        slug: 'all',
        name: '',
    });

    const fetchCookiesAndSetContent = () => {
        setLoading(true);
        const myContent = getMyContent();
        myContent && setContent(myContent);
    };

    const fetchData = async () => {
        if (content().length === 0) {
            setLoading(false);
            return setFetchedData([]);
        }

        const promises = content().map(async (element) => {
            try {
                const response = await graphQlClient(GET_CONTENT_QUERY, { key: element.permalink }, true);
                if (!response || !response.resource) {
                    return null;
                }
                return response;
            } catch (e) {
                console.log(e);
                return null;
            }
        });

        const results = await Promise.all(promises);
        setFetchedData(results.filter(Boolean));
        setLoading(false);
    };

    onMount(() => {
        fetchCookiesAndSetContent();
        fetchData();
    });

    const refetchDataOnRemoval = () => {
        fetchCookiesAndSetContent();
        fetchData();
    };

    createEffect(
        on(
            () => content(),
            () => fetchData(),
            {
                defer: true,
            }
        )
    );

    const fetchedContent = createMemo((): any => fetchedData().map((element: any) => {
        const content = element.resource.content;
        // WP-stored `url` on raw resource content is relative ("events/orange"); SolidJS Router
        // would otherwise resolve it against the current path (/my-account/bookmarks/...).
        const url: string | undefined = content?.url;
        if (typeof url === 'string' && url && !url.startsWith('/') && !url.includes('://')) {
            return { ...content, url: `/${url}` };
        }
        return content;
    }));

    const events = createMemo((): any => fetchedContent().filter((item: any) => item.type === 'event'));
    const news = createMemo((): any => fetchedContent().filter((item: any) => item.type === 'blog-and-news' && item.tags.includes('news')));
    const products = createMemo((): any => fetchedContent().filter((item: any) => item.type === 'product'));
    const videos = createMemo((): any => fetchedContent().filter((item: any) => item.type === 'video-item'));
    const blogs = createMemo((): any => fetchedContent().filter((item: any) => item.type === 'blog-and-news' && !item.tags.includes('news')));
    const pages = createMemo((): any => fetchedContent().filter((item: any) => item.type === 'page'));

    const categories = [
        { name: 'Events', fn: events, slug: 'events' },
        { name: 'News', fn: news, slug: 'news' },
        { name: 'Products', fn: products, slug: 'products' },
        { name: 'Videos', fn: videos, slug: 'videos' },
        { name: 'Blogs', fn: blogs, slug: 'blogs' },
        { name: 'Pages', fn: pages, slug: 'pages' },
    ];

    const nonEmptyCategories = () => categories.filter(({ fn }) => fn().find(() => true));

    const handleCategoryClick = (slug: string) => {
        if (slug === 'all') {
            setSelectedCategory({ slug: 'all', name: localize('all', 'All') });
            return;
        }
        const category = categories.find((c) => c.slug === slug);
        if (category) setSelectedCategory(category);
    };

    const tabnavItems = (): TabnavItem[] => [
        { key: 'all', label: localize('all', 'All') },
        ...nonEmptyCategories().map((c) => ({ key: c.slug, label: decodeHtmlEntities(c.name) })),
    ];

    type ShowMoreButtonProps = {
        currentPage: number;
        resultsPerPage: number;
        totalResults: number;
        onClick: () => void;
    };

    const ShowMoreButton = (props: ShowMoreButtonProps) => {
        return (
            <Show
                when={
                    Math.min(props.currentPage * props.resultsPerPage, props.totalResults) !== props.totalResults &&
                    AppState.viewport.width <= theme.breakpoints.MOBILE
                }
            >
                <div class="flex justify-center">
                    <button type="button" onClick={props.onClick} class="bg-none rounded-[4.29rem] cursor-pointer border-none font-atos-bold font-[800] uppercase text-center w-fit text-primary underline p-0 text-[1.43rem] tracking-[0] leading-[2.7rem] transition-colors duration-200 hover:text-primary-hover [&:hover_svg]:fill-primary-hover max-[480px]:text-[1.4rem] max-[480px]:tracking-[0.4px] max-[480px]:leading-[2.36rem] [&_svg]:mx-auto">
                        <div>{localize('show-more', 'show more')}</div>
                        <AngleDownIcon fill={theme.palette.primary} {...{ height: 2.14, width: 2.86 }} ariaHidden />
                    </button>
                </div>
            </Show>
        );
    };

    const Events = () => (
        <Show when={(selectedCategory().slug === 'all' || selectedCategory().slug === 'events') && events().length}>
            <div class="[container-type:inline-size] flex flex-col gap-cvi-6">
                <Heading tag="h3" variant="heading-3xl" noBlockSpacing>
                    {localize('events', 'Events')}
                </Heading>

                <div class="grid grid-rows-[auto] h-fit gap-x-[3.14rem] gap-y-[2.21rem] grid-cols-3 @max-[859px]:grid-cols-2 @max-[600px]:grid-cols-1 @max-[600px]:gap-x-[1.57rem] @max-[480px]:gap-x-[1.14rem] @max-[480px]:gap-y-[2.355rem]">
                    <For each={isMobile() ? events().slice(0, currentPage().events * POSTS_PER_PAGE_MOBILE) : events()} fallback={null}>
                        {(event: EventData) => (
                            <div onClick={() => refetchDataOnRemoval()} style={{ 'z-index': 0 }}>
                                {createGroupedEventCard({ event })}
                            </div>
                        )}
                    </For>
                </div>

                <ShowMoreButton
                    currentPage={currentPage().events}
                    resultsPerPage={POSTS_PER_PAGE_MOBILE}
                    totalResults={events().length}
                    onClick={() => setCurrentPage((prevPage) => ({ ...prevPage, events: prevPage.events + 1 }))}
                />
            </div>
        </Show>
    );

    const News = () => (
        <Show when={(selectedCategory().slug === 'all' || selectedCategory().slug === 'news') && news().length}>
            <div class="[container-type:inline-size] flex flex-col gap-cvi-6">
                <Heading tag="h3" variant="heading-3xl" noBlockSpacing>
                    {localize('news', 'News')}
                </Heading>

                <div class="grid grid-rows-[auto] h-fit gap-x-[3.14rem] gap-y-[2.21rem] grid-cols-3 @max-[859px]:grid-cols-2 @max-[600px]:grid-cols-1 @max-[600px]:gap-x-[1.57rem] @max-[480px]:gap-x-[1.14rem] @max-[480px]:gap-y-[2.355rem]">
                    <For each={isMobile() ? news().slice(0, currentPage().news * POSTS_PER_PAGE_MOBILE) : news()} fallback={null}>
                        {(post: BlogNewsData) => (
                            <div onClick={() => refetchDataOnRemoval()} style={{ 'z-index': 0 }}>
                                {createGroupedBlogAndNewsCard({ post })}
                            </div>
                        )}
                    </For>
                </div>

                <ShowMoreButton
                    currentPage={currentPage().news}
                    resultsPerPage={POSTS_PER_PAGE_MOBILE}
                    totalResults={news().length}
                    onClick={() => setCurrentPage((prevPage) => ({ ...prevPage, news: prevPage.news + 1 }))}
                />
            </div>
        </Show>
    );

    const Products = () => (
        <Show when={(selectedCategory().slug === 'all' || selectedCategory().slug === 'products') && products().length}>
            <div class="[container-type:inline-size] flex flex-col gap-cvi-6">
                <Heading tag="h3" variant="heading-3xl" noBlockSpacing>
                    {localize('products', 'Products')}
                </Heading>
                <div class="grid grid-rows-[auto] h-fit gap-x-[3.14rem] gap-y-[2.21rem] grid-cols-3 @max-[859px]:grid-cols-2 @max-[600px]:grid-cols-1 @max-[600px]:gap-x-[1.57rem] @max-[480px]:gap-x-[1.14rem] @max-[480px]:gap-y-[2.355rem]">
                    <For each={isMobile() ? products().slice(0, currentPage().products * POSTS_PER_PAGE_MOBILE) : products()} fallback={null}>
                        {(product: ProductData) => (
                            <div onClick={() => refetchDataOnRemoval()}>
                                {createGroupedProductCard(product)}
                            </div>
                        )}
                    </For>
                </div>

                <ShowMoreButton
                    currentPage={currentPage().products}
                    resultsPerPage={POSTS_PER_PAGE_MOBILE}
                    totalResults={products().length}
                    onClick={() => setCurrentPage((prevPage) => ({ ...prevPage, products: prevPage.products + 1 }))}
                />
            </div>
        </Show>
    );

    const Videos = () => (
        <Show when={(selectedCategory().slug === 'all' || selectedCategory().slug === 'videos') && videos().length}>
            <div class="[container-type:inline-size] flex flex-col gap-cvi-6">
                <Heading tag="h3" variant="heading-3xl" noBlockSpacing>
                    {localize('videos', 'Videos')}
                </Heading>
                <div class="grid grid-rows-[auto] h-fit gap-x-[3.14rem] gap-y-[2.21rem] grid-cols-3 @max-[859px]:grid-cols-2 @max-[600px]:grid-cols-1 @max-[600px]:gap-x-[1.57rem] @max-[480px]:gap-x-[1.14rem] @max-[480px]:gap-y-[2.355rem]">
                    <For each={isMobile() ? videos().slice(0, currentPage().videos * POSTS_PER_PAGE_MOBILE) : videos()} fallback={null}>
                        {(video: VideoData) => (
                            <div onClick={() => refetchDataOnRemoval()}>
                                {createGroupedVideoCard(video, true)}
                            </div>
                        )}
                    </For>
                </div>

                <ShowMoreButton
                    currentPage={currentPage().videos}
                    resultsPerPage={POSTS_PER_PAGE_MOBILE}
                    totalResults={videos().length}
                    onClick={() => setCurrentPage((prevPage) => ({ ...prevPage, videos: prevPage.videos + 1 }))}
                />
            </div>
        </Show>
    );

    const Blogs = () => (
        <Show when={(selectedCategory().slug === 'all' || selectedCategory().slug === 'blogs') && blogs().length}>
            <div class="[container-type:inline-size] flex flex-col gap-cvi-6">
                <Heading tag="h3" variant="heading-3xl" noBlockSpacing>
                    {localize('blogs', 'Blogs')}
                </Heading>
                <div class="grid grid-rows-[auto] h-fit gap-x-[3.14rem] gap-y-[2.21rem] grid-cols-3 @max-[859px]:grid-cols-2 @max-[600px]:grid-cols-1 @max-[600px]:gap-x-[1.57rem] @max-[480px]:gap-x-[1.14rem] @max-[480px]:gap-y-[2.355rem]">
                    <For each={isMobile() ? blogs().slice(0, currentPage().blogs * POSTS_PER_PAGE_MOBILE) : blogs()} fallback={null}>
                        {(post: BlogNewsData) => (
                            <div onClick={() => refetchDataOnRemoval()} style={{ 'z-index': 0 }}>
                                {createGroupedBlogAndNewsCard({ post })}
                            </div>
                        )}
                    </For>
                </div>

                <ShowMoreButton
                    currentPage={currentPage().blogs}
                    resultsPerPage={POSTS_PER_PAGE_MOBILE}
                    totalResults={blogs().length}
                    onClick={() => setCurrentPage((prevPage) => ({ ...prevPage, blogs: prevPage.blogs + 1 }))}
                />
            </div>
        </Show>
    );

    const Pages = () => (
        <Show when={(selectedCategory().slug === 'all' || selectedCategory().slug === 'pages') && pages().length}>
            <div class="[container-type:inline-size] flex flex-col gap-cvi-6">
                <Heading tag="h3" variant="heading-3xl" noBlockSpacing>
                    {localize('pages', 'Pages')}
                </Heading>
                <div class="grid grid-rows-[auto] h-fit gap-x-[3.14rem] gap-y-[2.21rem] grid-cols-3 @max-[859px]:grid-cols-2 @max-[600px]:grid-cols-1 @max-[600px]:gap-x-[1.57rem] @max-[480px]:gap-x-[1.14rem] @max-[480px]:gap-y-[2.355rem]">
                    <For each={isMobile() ? pages().slice(0, currentPage().pages * POSTS_PER_PAGE_MOBILE) : pages()} fallback={null}>
                        {(post: any) => (
                            <div onClick={() => refetchDataOnRemoval()}>
                                <PagePostCard data={post} />
                            </div>
                        )}
                    </For>
                </div>

                <ShowMoreButton
                    currentPage={currentPage().pages}
                    resultsPerPage={POSTS_PER_PAGE_MOBILE}
                    totalResults={pages().length}
                    onClick={() => setCurrentPage((prevPage) => ({ ...prevPage, pages: prevPage.pages + 1 }))}
                />
            </div>
        </Show>
    );

    const DummyButtonForTabbing = () => (
        // We need something to focus as the last element (the alternative is to figure out the last element in the list of lists - not worth it)
        <button
            style={{ width: 0, height: 0, opacity: 0 }}
            onClick={() => {}}
            ref={(el: HTMLElement) => props.lastElement && props.lastElement(el)}
            aria-hidden="true"
        />
    );

    return (
        <div class="flex flex-col sm:gap-cvi-11 gap-cvi-9 [container-type:inline-size]">
            <Heading tag="h2" variant="heading-4xl" noBlockSpacing>
                {localize('bookmarks', 'Bookmarks')}
            </Heading>

            <div class="rounded-cvi-xl">
                <Tabnav
                    tabs={tabnavItems()}
                    activeKey={selectedCategory().slug}
                    onChange={handleCategoryClick}
                    ariaLabel={localize('categories', 'Categories')}
                    overflowAriaLabel={localize('more-categories', 'More categories')}
                />
            </div>

            <Show when={loading()}>
                <Heading align="center" tag="h4">{localize('loading', 'Loading...')}</Heading>
            </Show>

            <Show when={!loading() && !nonEmptyCategories().length}>
                <Text fontSize="normal">{localize('no-results', 'No results found')}</Text>
            </Show>

            <Events />
            <News />
            <Products />
            <Videos />
            <Blogs />
            <Pages />

            <DummyButtonForTabbing />
        </div>
    );
};
